import React, { useRef, useState, useEffect } from 'react';
import './styles.scss';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { scheme } from './validation';
import dayjs from 'dayjs';
import {
  Divider,
  Flex,
  Group,
  LoadingOverlay,
  Select,
  Text,
  TextInput,
  Textarea
} from '@mantine/core';
import * as Unicons from '@iconscout/react-unicons';
import Button from '../../components/elements/Button';
import { DatePickerInput } from '@mantine/dates';
import { saveImage } from '../../services/inventoryReward';
import { useMutation } from '@tanstack/react-query';
import { showNotification } from '../../utilities/mantineNotification';
import DropzoneInput from '../../components/elements/DropzoneInput';

interface FormInventoryRewardProps {
  type?: string;
  data?: any;
  loading?: boolean;
  onClose: () => void;
  handleSubmitData: (data: any) => void;
}

interface FormInventoryRewardData {
  inventoryName?: string;
  rewardImage?: string;
  redeemPoint?: number;
  type?: string;
  stockReward?: number;
  startDate?: string;
  endDate?: string;
  description?: string;
  status?: string;
  maxRedeem?: number;
}

interface YupFormData {
  inventoryName?: string;
  rewardImage?: string;
  redeemPoint?: string;
  type?: string;
  stockReward?: string;
  startDate?: Date;
  endDate?: Date;
  description?: string;
  status?: string;
  maxRedeem?: string;
}

const FormInventoryReward: React.FC<FormInventoryRewardProps> = ({
  onClose,
  loading,
  type,
  data,
  handleSubmitData
}) => {
  const [valueStartDate, setValueStartDate] = useState<Date | null>(
    data?.startDate ? new Date(data?.startDate) : null
  );

  const [valueEndDate, setValueEndDate] = useState<Date | null>(
    data?.endDate ? new Date(data?.endDate) : null
  );

  const {
    control,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<YupFormData>({
    defaultValues: {
      inventoryName: data?.inventoryName || '',
      rewardImage: data?.rewardImage || '',
      redeemPoint: data?.redeemPoint || '',
      stockReward: data?.stockReward || '',
      startDate: data?.startDate || '',
      endDate: data?.endDate || '',
      description: data?.description || '',
      status: data?.status || '',
      maxRedeem: data?.maxRedeem || ''
    },
    mode: 'onBlur',
    resolver: yupResolver(scheme)
  });

  const handleChangeDate = (date: any, type: string, field: any) => {
    field.onChange(date);
    if (type === 'startDate') setValueStartDate(date);
    if (type === 'endDate') setValueEndDate(date);
  };

  const handleSubmitForm: SubmitHandler<YupFormData> = (values) => {
    const payload: FormInventoryRewardData = {
      ...values,
      startDate: dayjs(valueStartDate).format('YYYY-MM-DD'),
      endDate: dayjs(valueEndDate).format('YYYY-MM-DD'),
      stockReward: Number(values.stockReward),
      maxRedeem: Number(values.maxRedeem),
      redeemPoint: Number(values.redeemPoint)
    };
    handleSubmitData(payload);
  };

  const handleDrop = (files: File[], base64: string, field: any) => {
    const rewardImage = { fileName: files[0].name, base64: base64 };
    field.onChange(JSON.stringify(rewardImage));
  };

  return (
    <>
      <section className="master-category-content__form">
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <TextInput
            required
            label="Inventory Reward Name"
            radius={10}
            withAsterisk
            placeholder="Input inventory reward name"
            error={errors.inventoryName?.message}
            {...register('inventoryName')}
          />
          <Textarea
            required
            label="Description"
            radius={10}
            withAsterisk
            placeholder="Input description"
            error={errors.description?.message}
            {...register('description')}
          />
          <Text mt={16} className="text-xs-semibold" c={'gray'}>
            Reward Section
          </Text>
          <Divider mb={8} />
          <TextInput
            required
            label="Stock Inventory Reward"
            radius={10}
            withAsterisk
            placeholder="Input stock inventory reward"
            error={errors.stockReward?.message}
            type="number"
            {...register('stockReward')}
          />

          <Controller
            control={control}
            rules={{
              required: true
            }}
            render={({ field }) => (
              <DropzoneInput
                label="Reward Image"
                withAsterik
                onHandleDrop={(value, base64) =>
                  handleDrop(value, base64, field)
                }
                error={errors?.rewardImage?.message || ''}
                defaultValue={data?.rewardImage || null}
              />
            )}
            name="rewardImage"
          />

          <Text mt={16} className="text-xs-semibold" c={'gray'}>
            Redeem Section
          </Text>
          <Divider mb={8} />
          <Group>
            <Flex flex={1} direction="column">
              <TextInput
                required
                label="Maximum Redeem Per User"
                radius={10}
                withAsterisk
                placeholder="Input max redeem per user"
                error={errors.maxRedeem?.message}
                type="number"
                {...register('maxRedeem')}
              />
            </Flex>
            <Flex flex={1} direction="column">
              <TextInput
                required
                label="Redeem Point"
                radius={10}
                withAsterisk
                placeholder="Input redeem point"
                error={errors.redeemPoint?.message}
                type="number"
                {...register('redeemPoint')}
              />
            </Flex>
          </Group>

          <Text mt={16} className="text-xs-semibold" c={'gray'}>
            Period Section
          </Text>
          <Divider mb={8} />
          <Group>
            <Controller
              control={control}
              rules={{
                required: true
              }}
              render={({ field }) => (
                <Flex flex={1} direction="column">
                  <DatePickerInput
                    className={
                      errors.startDate?.message &&
                      'schedule-rater__form__selectinput--error'
                    }
                    style={{
                      width: '100%'
                    }}
                    label="Start Date"
                    withAsterisk
                    placeholder="Select Date"
                    onChange={(date) =>
                      handleChangeDate(date, 'startDate', field)
                    }
                    minDate={dayjs().toDate()}
                    value={valueStartDate}
                    rightSection={<Unicons.UilCalendarAlt size={20} />}
                  />
                  {errors?.startDate?.message && (
                    <Text size="xs" c="red" fw={400}>
                      {errors.startDate.message}
                    </Text>
                  )}
                </Flex>
              )}
              name="startDate"
            />

            <Controller
              control={control}
              rules={{
                required: true
              }}
              render={({ field }) => (
                <Flex flex={1} direction="column">
                  <DatePickerInput
                    className={
                      errors.endDate?.message &&
                      'schedule-rater__form__selectinput--error'
                    }
                    style={{
                      width: '100%'
                    }}
                    label="End Date"
                    withAsterisk
                    placeholder="Select Date"
                    onChange={(date) =>
                      handleChangeDate(date, 'endDate', field)
                    }
                    minDate={dayjs().toDate()}
                    value={valueEndDate}
                    rightSection={<Unicons.UilCalendarAlt size={20} />}
                  />
                  {errors?.endDate?.message && (
                    <Text size="xs" c="red" fw={400}>
                      {errors.endDate.message}
                    </Text>
                  )}
                </Flex>
              )}
              name="endDate"
            />
          </Group>

          <Text mt={16} className="text-xs-semibold" c={'gray'}>
            Status Section
          </Text>
          <Divider mb={8} />
          <Controller
            control={control}
            rules={{
              required: true
            }}
            render={({ field }) => (
              <div>
                <Select
                  className={
                    errors.status?.message &&
                    'master-rater__form__selectinput--error'
                  }
                  label="Status"
                  withAsterisk
                  placeholder="Select Status"
                  onChange={field.onChange}
                  value={field.value}
                  data={[
                    { label: 'Active', value: 'Y' },
                    { label: 'InActive', value: 'N' }
                  ]}
                />
                {errors?.status?.message && (
                  <Text size="xs" c="red" fw={400}>
                    {errors.status.message}
                  </Text>
                )}
              </div>
            )}
            name="status"
          />

          <Flex justify="flex-end" align="center" gap={16} mt={32}>
            <Button
              variant="error-beelajar"
              disabled={loading}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button disabled={loading} loading={loading} type="submit">
              {type === 'add' ? 'Submit' : 'Update'}
            </Button>
          </Flex>
        </form>
      </section>
    </>
  );
};

export default FormInventoryReward;
